import React from 'react';
import { 
  Film, Monitor, Waves, Armchair, Lightbulb, PaintBucket, 
  Mic2, Projector, Sofa, Smartphone, Network, Settings, Wrench, ArrowRight 
} from 'lucide-react';
import { Button } from './ui/button';

const services = [
  {
    id: 1,
    title: 'Theatre Consultation & Planning',
    icon: 'Film',
    description: 'Foundation of every project - we design the right system before selling equipment.',
    features: [
      'Requirement analysis & lifestyle consultation',
      'Room size and acoustic feasibility study',
      'Budget planning & system recommendation',
      '2D / 3D layout planning',
      'Screen size & projector selection guidance'
    ],
    image: 'https://images.unsplash.com/photo-1568057373106-63057e421d1c?w=600&q=80'
  },
  {
    id: 2,
    title: 'Acoustic Design & Sound Treatment',
    icon: 'Waves',
    description: 'The most important aspect - proper acoustics make or break your theatre experience.',
    features: [
      'Acoustic analysis of room',
      'Wall panel design (fabric, wooden, diffuser)',
      'Soundproofing solutions',
      'Bass control & reflection management',
      'Ceiling acoustic treatments'
    ],
    image: 'https://images.unsplash.com/photo-1621542864146-46c51689f19f?w=600&q=80'
  },
  {
    id: 3,
    title: 'Audio System Design & Installation',
    icon: 'Mic2',
    description: 'Professional Dolby Atmos systems with premium brands like KEF, Polk, Focal, Klipsch.',
    features: [
      'Dolby Atmos design (5.1 / 7.1 / 7.2.4)',
      'Speaker placement & calibration',
      'In-wall / in-ceiling speaker installation',
      'Subwoofer optimization',
      'Professional sound tuning'
    ],
    image: 'https://images.unsplash.com/photo-1645536729519-134e3b7e9e88?w=600&q=80'
  },
  {
    id: 4,
    title: 'Video & Projection Solutions',
    icon: 'Projector',
    description: 'Crystal clear 4K/Laser projection with perfect image calibration.',
    features: [
      'Projector selection & installation',
      'Projection screen supply (fixed, motorized, ALR)',
      'TV wall theatre setups',
      '4K / Laser projector configuration',
      'Image calibration'
    ],
    image: 'https://images.unsplash.com/photo-1743685889437-210ad44b6c5f?w=600&q=80'
  },
  {
    id: 5,
    title: 'Lighting & Ambience Integration',
    icon: 'Lightbulb',
    description: 'Create the perfect cinema atmosphere with mood lighting and star ceilings.',
    features: [
      'Theatre mood lighting design',
      'Star ceiling installation',
      'Step lights & cove lighting',
      'Scene-based lighting automation',
      'Cinema ambience programming'
    ],
    image: 'https://images.unsplash.com/photo-1653336251629-9881d5465e42?w=600&q=80'
  },
  {
    id: 6,
    title: 'Interior & Theatre Room Design',
    icon: 'PaintBucket',
    description: 'Complete theatre aesthetics with acoustic panels, seating, and cinema-style finishes.',
    features: [
      'Acoustic wall panel fabrication',
      'Theatre false ceiling',
      'Platform & riser construction',
      'Recliner seating layout',
      'Cinema-style aesthetics'
    ],
    image: 'https://images.unsplash.com/photo-1746439324737-2c9f9a3e81a6?w=600&q=80'
  },
  {
    id: 7,
    title: 'Calibration & System Optimization',
    icon: 'Settings',
    description: 'Fine-tune your system for perfect audio-visual performance.',
    features: [
      'Audio calibration (SPL tuning)',
      'Video color calibration',
      'Dolby Atmos configuration',
      'Source device optimization',
      'Performance testing'
    ],
    image: 'https://images.unsplash.com/photo-1698884293353-6772a02d1005?w=600&q=80'
  },
  {
    id: 8,
    title: 'Maintenance & After-Sales Service',
    icon: 'Wrench',
    description: 'Comprehensive support with AMC, updates, troubleshooting, and upgrades.',
    features: [
      'Annual maintenance contracts (AMC)',
      'Software updates',
      'System troubleshooting',
      'Equipment upgrades',
      'Remote support'
    ],
    image: 'https://images.unsplash.com/photo-1553775282-20af80779df7?w=600&q=80'
  }
];

const iconMap = {
  Film: Film,
  Monitor: Monitor,
  Waves: Waves,
  Armchair: Armchair,
  Lightbulb: Lightbulb,
  PaintBucket: PaintBucket,
  Mic2: Mic2,
  Projector: Projector,
  Sofa: Sofa,
  Smartphone: Smartphone,
  Network: Network,
  Settings: Settings,
  Wrench: Wrench,
};

const Services = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-24 bg-gray-50 relative">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <div className="text-cyan-800 text-sm font-semibold tracking-wider uppercase mb-4">
            OUR SERVICES
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Complete Home Theatre Solutions
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            From consultation to installation and maintenance, we deliver end-to-end home theatre 
            solutions tailored to create your perfect cinema experience.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon] || Film;
            return (
              <div
                key={service.id}
                className="group relative bg-white rounded-2xl overflow-hidden border-2 border-cyan-700/30 hover:border-cyan-700 hover:shadow-xl transition-all duration-300 flex flex-col h-full"
              >
                {/* Image */}
                <div className="relative h-40 overflow-hidden flex-shrink-0">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-3 right-3 w-10 h-10 bg-cyan-800 rounded-full flex items-center justify-center shadow-lg">
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  
                  {/* Title on image */}
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="text-white font-bold text-base leading-tight">{service.title}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-grow">
                  <p className="text-gray-600 text-sm mb-4 h-10 line-clamp-2">{service.description}</p>

                  {/* Features */}
                  <div className="space-y-2 mb-4 flex-grow">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <div
                        key={idx}
                        className="text-xs text-cyan-800 flex items-center gap-2"
                      >
                        <div className="w-1.5 h-1.5 bg-cyan-800 rounded-full flex-shrink-0"></div>
                        <span className="line-clamp-1">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    variant="ghost"
                    className="text-cyan-800 hover:text-cyan-900 hover:bg-cyan-50 p-0 h-auto text-sm font-semibold group/btn mt-auto"
                    onClick={() => scrollToSection('contact')}
                  >
                    Learn More
                    <ArrowRight className="ml-1 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Button
            onClick={() => scrollToSection('contact')}
            className="bg-white border-2 border-cyan-800 text-cyan-800 hover:bg-cyan-50 px-8 py-6 text-lg rounded-full transition-all duration-200"
          >
            Get Free Consultation
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;
